// @ts-check

/** @typedef {?{push: function}} DataLayer */

/**
 *
 * @param {DataLayer} dataLayer
 * @returns
 */
export default function enableGooglePushEvents(dataLayer) {
  if (!dataLayer) {
    return;
  }
  enableClickEvents(dataLayer);
  enableSubmitEvents(dataLayer);
  enableFaqSearchEvents(dataLayer);
  enableDocumentLoadEvent(dataLayer);
}

/**
 *
 * @param {DataLayer} dataLayer
 * @param {string} action
 * @param {string=} label
 * @param {string=} category
 */
function pushEvent(dataLayer, action, label = null, category = null) {
  dataLayer.push({
    event: "gtmEvent",
    eventCategory: category || "buttonClick",
    eventAction: action,
    eventLabel: label,
    eventNonInteraction: false,
  });
}

/**
 *
 * @param {DataLayer} dataLayer
 */
function enableDocumentLoadEvent(dataLayer) {
  const el = /** @type {?HTMLElement} */ (document.querySelector('[data-google-event-on="document-load"]'));
  if (!el) { return; }
  const data = el.dataset;
  pushEvent(dataLayer, data.googleEventAction, data.googleEventLabel, data.googleEventCategory);
}

/**
 * // push event on input in FAQ search field
 * @param {DataLayer} dataLayer 
 */
function enableFaqSearchEvents(dataLayer) {
  const faqSearch = document.querySelector("#st-search-input");
  const onSearch = function (/** @type {InputEvent} */ e) {
    const input = /** @type {HTMLInputElement} */ (e.target);
    const value = input.value;
    if (value.length) {
      pushEvent(dataLayer, "Search", value);
    }
  };
  const debouncedOnSearch = debounced(1000, onSearch);
  if (faqSearch) {
    faqSearch.addEventListener("input", debouncedOnSearch);
  }
}

/**
 * push events on submit of forms - Add member, resource, plan, space
 * @param {DataLayer} dataLayer
 */
function enableSubmitEvents(dataLayer) {
  document.addEventListener("submit", function (e) {
    const el = /** @type {HTMLElement} */ (e.target);
    const dataTag = el.dataset.googleEvent;
    switch (dataTag) {
      case "Save Plan":
        const planNotHiddenCheckbox = /** @type {HTMLInputElement} */ (
          document.getElementById("plan_hidden_false")
        );
        if (planNotHiddenCheckbox.checked) {
          // only send event when public plan is saved
          pushEvent(dataLayer, "Save Public Plan");
        }
        break;
      default:
        if (dataTag) {
            pushEvent(dataLayer, dataTag);
        }
        break;
    }
  });
}

/**
 * push events for link clicks - Free Trial, Sign Up, Contact us
 * @param {DataLayer} dataLayer
 */
function enableClickEvents(dataLayer) {
  document.addEventListener(
    "click",
    function handleEvent(/** @type {MouseEvent} */ e) {
      const el = /** @type {HTMLElement} */ (e.target);
      const dataTag = el.dataset.googleEvent;
      switch (dataTag) {
        case "Free Trial":
          var location = el.dataset.location // if more than one button on page, send location
          pushEvent(dataLayer, "Get Started for Free", location);
          break;
        case "Sign Up":
          var label = el.title; // level of subscription, discount info
          pushEvent(dataLayer, "Sign up", label);
          break;
        case "Contact Us":
          pushEvent(dataLayer, "Contact us for details");
          break;
        default:
          if (dataTag) {
            pushEvent(dataLayer, dataTag, el.dataset.location);
          }
          break;
      }
    }
  );
}

/**
 * see https://codeburst.io/throttling-and-debouncing-in-javascript-646d076d0a44
 * @param {number} delay
 * @param {function} fn
 * @returns {EventListener}
 */
function debounced(delay, fn) {
  /**
   * @type {number?}
   */
  let timerId;
  return function (...args) {
    if (timerId) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(() => {
      fn(...args);
      timerId = null;
    }, delay);
  };
}
